import { useIsInViewport } from '@/hooks';
import { useEffect, useRef } from 'react';
import { minutesToMilliseconds } from 'date-fns';
import { type UseQuery, UseQueryOptions } from '@/store';
import { useIsTabFocused } from '@/modules/ui';

type UsePollingWhenInViewArg<T, QARG> = {
  elementId: string | undefined;
  useQuery: UseQuery<T, QARG>;
  queryArg: QARG;
  queryConfig?: UseQueryOptions<T, QARG>;
  pollingMs?: number;
  skipWhileOut?: boolean;
  refetchWhenIn?: boolean;
};

export const usePollingWhenInView = <T, ARG>({
  elementId,
  useQuery,
  queryArg,
  queryConfig,
  pollingMs,
  skipWhileOut,
  refetchWhenIn
}: UsePollingWhenInViewArg<T, ARG>) => {
  const isTabFocused = useIsTabFocused();
  const { isVisible: elementVisible, isShownAtLeastOnce: elementShownOnce } =
    useIsInViewport(elementId);

  const pollingInterval = isTabFocused
    ? elementVisible
      ? pollingMs
      : queryConfig?.pollingInterval
    : undefined;

  const result = useQuery(queryArg, {
    ...queryConfig,
    skip: (skipWhileOut && !elementShownOnce) || queryConfig?.skip,
    pollingInterval
  });

  const refetch = useRef(result.refetch);
  const lastFulfilled = result.fulfilledTimeStamp;

  useEffect(() => {
    if (elementVisible && refetchWhenIn && lastFulfilled) {
      const minDiff = pollingInterval ?? minutesToMilliseconds(5);
      if (Date.now() - lastFulfilled > minDiff) {
        refetch.current?.();
      }
    }
  }, [elementVisible, refetchWhenIn, lastFulfilled, pollingInterval]);

  return result;
};
